import React, { useEffect, useState } from 'react';
import { Button } from 'components/ui/button';
import { Field } from '@buildoutinc/blueprint-react/ui/Field';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Input } from '@buildoutinc/blueprint-react/ui/Input';
import { InputGroup } from '@buildoutinc/blueprint-react/ui/InputGroup';
import { Modal } from '@buildoutinc/blueprint-react/ui/Modal';
import { faGlobe, faLink } from '@fortawesome/pro-regular-svg-icons';

interface Props {
  open: boolean;
  initialUrl?: string;
  // When the photo is already marked altered the modal manages it: same link field, plus a remove action.
  managing?: boolean;
  onConfirm: (originalUrl: string) => void;
  onRemove?: () => void;
  onClose: () => void;
}

// Accept a full http(s) URL or a bare domain (e.g. "buildout.com"), assuming https when no scheme is
// given. A real domain (a dot in the host) is required, so "buildout" alone is rejected.
function parseHttpUrl(value: string): URL | null {
  const trimmed = value.trim();
  if (!trimmed) return null;

  const withScheme = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;

  try {
    const url = new URL(withScheme);
    const isHttp = url.protocol === 'http:' || url.protocol === 'https:';
    return isHttp && url.hostname.includes('.') ? url : null;
  } catch {
    return null;
  }
}

export default function MarkDigitallyAlteredModal({
  open, initialUrl = '', managing = false, onConfirm, onRemove, onClose,
}: Props) {
  const [value, setValue] = useState('');
  const [touched, setTouched] = useState(false);

  // Reset to the photo's current link each time the modal opens.
  useEffect(() => {
    if (open) {
      setValue(initialUrl);
      setTouched(false);
    }
  }, [open]);

  const parsed = parseHttpUrl(value);
  const isValid = Boolean(parsed);
  const showError = touched && value.trim().length > 0 && !isValid;

  const handleConfirm = () => {
    if (!parsed) return;

    onConfirm(parsed.href);
    onClose();
  };

  const handleRemove = () => {
    onRemove?.();
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Modal.Content centered>
        <Modal.Header>
          <Modal.Title>{managing ? 'Manage digitally altered' : 'Mark image as digitally altered'}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <p className="text-muted mb-3">
            Link to where the unaltered original can be viewed. Users will scan a QR code to access.
          </p>
          <Field className="form-group required">
            <Field.Label htmlFor="digitallyAlteredOriginalUrl">Link to unaltered original</Field.Label>
            <InputGroup>
              <InputGroup.Addon>
                <FontAwesomeIcon icon={faLink} />
              </InputGroup.Addon>
              <Input
                aria-invalid={showError}
                autoFocus
                id="digitallyAlteredOriginalUrl"
                placeholder="https://www.dropbox.com/scl/fi/.../original.jpg"
                type="url"
                value={value}
                onBlur={() => setTouched(true)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValue(e.target.value)}
              />
            </InputGroup>
            {showError && (
              <span className="d-block mt-1 small text-danger">
                Enter a valid URL, e.g. https://www.dropbox.com/s/.../original.jpg
              </span>
            )}
            {isValid && parsed && (
              <span className="d-flex align-items-center gap-1 mt-1 small text-muted">
                <FontAwesomeIcon icon={faGlobe} /> Links out to {parsed.host}
              </span>
            )}
          </Field>
        </Modal.Body>

        <Modal.Footer>
          {managing && (
            <Button
              className="me-auto text-danger"
              label="Remove altered flag"
              variant="ghost"
              onClick={handleRemove}
            />
          )}
          <Button label="Cancel" variant="ghost" onClick={onClose} />
          <Button disabled={!isValid} label={managing ? 'Save link' : 'Mark as altered'} onClick={handleConfirm} />
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
}
